import { useMemo, useRef, useState } from 'react';
import type { HistoryData } from '~/vendor/cxy/types';
import {
  Bar,
  BarChart as RechartBarChart,
  Cell,
  Label,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

import { Slider } from '../Slider';

import styles from './styles/BarChart.module.scss';
import { type BarChartProps, ChartVariant } from './util/types';
import { useBarChart } from './util/useBarChart';
import { useContainerQuery } from './util/useContainerQuery';
import { dateXAxisProps, primaryXAxisProps } from './util/xAxisProps';
import { LABEL_STYLES, ReferenceLineLabel } from './ReferenceLineLabel';
import { TabFilter } from './TabFilter';

export function BarChart({
  alternativeLines = 'Alternative Lines',
  americanFormat = false,
  barRadius = 0,
  customLabels = { All: 'All', Home: 'Home', Away: 'Away', versus: 'vs' },
  flipBarSuccess = false,
  fullTabsWidth = false,
  historyData,
  limit,
  line,
  oppositionTeam,
  trackEvent,
  variant = ChartVariant.DEFAULT,
}: BarChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  // Finite, not truthy and not merely non-nullish. `line || null` filed a legitimate ZERO line as
  // absent; `line ?? null` fixes that but admits NaN, and callers reach this through
  // `Number(data.line?.absolute)`, which is NaN whenever the field is missing — that rendered the
  // threshold slider and reference line on charts that have no line at all.
  const [thresholdLine, setThresholdLine] = useState<number | null>(Number.isFinite(line) ? Number(line) : null);
  const { changeFilterType, filteredData, maxBetStat, selectedFilterType, shouldShowVersusTab, yAxisWidth } =
    useBarChart({
      historyData,
      limit,
      oppositionTeam,
      trackEvent,
    });

  const isCompact = variant === ChartVariant.COMPACT;
  const isContainerWide = useContainerQuery(containerRef, 769);

  const yAxisTicks = useMemo(() => {
    return Array.from({ length: maxBetStat + 1 }, (_, i) => i);
  }, [maxBetStat]);

  const chartMargin = useMemo(() => {
    const defaultMargins = {
      top: 10,
      right: 0,
      left: 0,
      bottom: 0,
    };

    if (thresholdLine === null) {
      return defaultMargins;
    }

    const maxValueLength = maxBetStat.toString().length + 1;
    const rightMargin = maxValueLength * LABEL_STYLES.CHAR_WIDTH + LABEL_STYLES.PADDING;

    return {
      ...defaultMargins,
      right: rightMargin,
    };
  }, [thresholdLine, maxBetStat]);

  const getCellClassName = (entry: HistoryData, line?: number, thresholdLine = 0, flipBarSuccess = false): string => {
    // `Number.isFinite`, not `!== undefined`: callers reach `line` through
    // `Number(data.line?.absolute)`, which is NaN when the field is absent, and NaN is not
    // undefined — so a lineless chart took the threshold branch and inverted its bar colours. Same
    // defect as `thresholdLine` above; fixing one and not the other is why this shipped.
    if (Number.isFinite(line)) {
      const isAboveThreshold = entry.participant.betStat > thresholdLine;
      return isAboveThreshold
        ? flipBarSuccess
          ? 'bar-below'
          : 'bar-above'
        : flipBarSuccess
          ? 'bar-above'
          : 'bar-below';
    }

    return entry.participant.betResult === 'WON' ? 'bar-above' : 'bar-below';
  };

  const handleSliderChange = (value: number[]) => {
    trackEvent?.('lines_selection', {
      eventLabel: 'Lines Selection',
      selection: value[0],
    });
    setThresholdLine(value[0]);
  };

  return (
    <div className={styles.wrapper} data-cxy-name="bar-chart" ref={containerRef}>
      <div>
        {!isCompact && (
          <TabFilter
            customLabels={customLabels}
            fullTabsWidth={fullTabsWidth}
            oppositionTeam={oppositionTeam}
            selectedFilterType={selectedFilterType}
            setSelectedFilterType={changeFilterType}
            shouldShowVersusTab={shouldShowVersusTab}
          />
        )}
        <div className={isCompact ? styles.compactBarChartWrapper : styles.barChartWrapper}>
          <ResponsiveContainer height="100%" width="100%">
            <RechartBarChart data={filteredData} margin={chartMargin}>
              {!isCompact && (
                <>
                  <XAxis {...primaryXAxisProps({ americanFormat, isContainerWide })} />
                  <XAxis {...dateXAxisProps({ americanFormat })} />
                </>
              )}
              <YAxis
                axisLine={false}
                domain={[0, maxBetStat]}
                fontSize={12}
                tickLine={false}
                ticks={yAxisTicks}
                width={yAxisWidth}
              />
              <Bar dataKey="participant.betStat" radius={barRadius}>
                {filteredData.map((entry, index) => (
                  <Cell
                    className={getCellClassName(entry, line, thresholdLine ?? 0, flipBarSuccess)}
                    key={`cell-${index}`}
                  />
                ))}
              </Bar>
              {thresholdLine !== null && (
                <ReferenceLine y={thresholdLine}>
                  <Label content={props => <ReferenceLineLabel {...props} value={thresholdLine} />} />
                </ReferenceLine>
              )}
            </RechartBarChart>
          </ResponsiveContainer>
        </div>
        {!isCompact && (
          <div className={styles.otherLines}>
            <div className={styles.alternativeLines}>
              {thresholdLine !== null && (
                <Slider
                  className={styles.slider}
                  label={alternativeLines}
                  max={maxBetStat}
                  min={0}
                  onValueChange={handleSliderChange}
                  shouldShowBackground
                  shouldShowLabel
                  step={0.5}
                  value={[thresholdLine]}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
